import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Icon } from "@/lib/icons";
import Logo from "@/components/Logo";

const GOALS = [
  { key: "build_strength", label: "Build Strength", icon: "Dumbbell" },
  { key: "lose_weight", label: "Lose Weight", icon: "Scale" },
  { key: "improve_mobility", label: "Improve Mobility", icon: "Move" },
  { key: "feel_better", label: "Feel Better", icon: "Heart" },
  { key: "start_again", label: "Start Again", icon: "RotateCcw" },
];
const LOCATIONS = ["At home", "At a gym", "Outdoors", "A mix"];
const AGES = ["45–54", "55–64", "65+", "Prefer not to say"];

export default function Onboarding() {
  const { user, ready, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [goal, setGoal] = useState(null);
  const [location, setLocation] = useState(null);
  const [ageRange, setAgeRange] = useState(null);
  const [interests, setInterests] = useState([]);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ready && user === false) navigate("/signin");
  }, [ready, user, navigate]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const toggleInterest = (slug) =>
    setInterests((p) => (p.includes(slug) ? p.filter((s) => s !== slug) : [...p, slug]));

  const finish = async () => {
    setSaving(true);
    try {
      await api.put("/auth/onboarding", { goal, location, age_range: ageRange, interests });
      await refreshUser();
      navigate("/community");
    } finally { setSaving(false); }
  };

  const Pill = ({ active, children, ...p }) => (
    <button {...p} className={`rounded-2xl border-2 px-5 py-3 text-[15px] font-medium transition-colors text-left
      ${active ? "border-teal bg-teal/10 text-teal" : "border-rose/50 bg-white text-plum-ink hover:border-teal/60"}`}>
      {children}
    </button>
  );

  return (
    <div className="min-h-screen bg-cream py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <Logo />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 bg-white rounded-3xl border border-rose/30 shadow-soft p-6 sm:p-10">
          <h1 className="font-display text-3xl text-plum">Let's personalise your Circle</h1>
          <p className="mt-2 text-mutedplum">A few quick taps and we'll tune your feed. You can change these anytime.</p>

          <div className="mt-8">
            <h2 className="font-display text-xl text-plum mb-3">What's your main goal right now?</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {GOALS.map((g) => (
                <button key={g.key} onClick={() => setGoal(g.key)} data-testid={`onb-goal-${g.key}`}
                  className={`rounded-2xl border-2 p-4 flex flex-col items-center gap-2 transition-colors
                    ${goal === g.key ? "border-teal bg-teal/10" : "border-rose/50 bg-white hover:border-teal/60"}`}>
                  <span className={`inline-flex h-11 w-11 items-center justify-center rounded-full ${goal === g.key ? "bg-teal text-white" : "bg-rose/30 text-teal"}`}>
                    <Icon name={g.icon} className="h-5 w-5" />
                  </span>
                  <span className="text-sm font-semibold text-plum text-center leading-tight">{g.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <h2 className="font-display text-xl text-plum mb-3">Where do you like to train?</h2>
            <div className="grid grid-cols-2 gap-3">
              {LOCATIONS.map((l) => <Pill key={l} active={location === l} onClick={() => setLocation(l)} data-testid={`onb-loc-${l}`}>{l}</Pill>)}
            </div>
          </div>

          <div className="mt-8">
            <h2 className="font-display text-xl text-plum mb-3">Your age range</h2>
            <div className="grid grid-cols-2 gap-3">
              {AGES.map((a) => <Pill key={a} active={ageRange === a} onClick={() => setAgeRange(a)} data-testid={`onb-age-${a}`}>{a}</Pill>)}
            </div>
          </div>

          <div className="mt-8">
            <h2 className="font-display text-xl text-plum mb-1">Topics you care about</h2>
            <p className="text-sm text-mutedplum mb-3">Pick any that interest you (for your feed & newsletter tags).</p>
            <div className="flex flex-wrap gap-2">
              {cats.map((c) => (
                <button key={c.slug} onClick={() => toggleInterest(c.slug)} data-testid={`onb-interest-${c.slug}`}
                  className={`rounded-full border-2 px-4 py-2 text-sm font-medium transition-colors inline-flex items-center gap-1.5
                    ${interests.includes(c.slug) ? "border-teal bg-teal text-white" : "border-rose/50 bg-white text-plum-ink hover:border-teal/60"}`}>
                  {interests.includes(c.slug) && <Check className="h-3.5 w-3.5" />}{c.name}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-10 flex items-center justify-between gap-4">
            <button onClick={() => navigate("/community")} className="text-mutedplum hover:text-teal text-[15px]" data-testid="onb-skip">Skip for now</button>
            <button onClick={finish} disabled={saving || !goal} data-testid="onb-finish"
              className="inline-flex items-center gap-2 rounded-full bg-teal px-8 py-3.5 font-semibold text-white hover:bg-teal-dark transition-colors disabled:opacity-50">
              {saving ? "Saving…" : "Enter the Circle"} <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
